import type { components } from '~/generated/types'
import { at, synthesizeRamp } from './colorRamp'

type ThemeColors = Pick<components['schemas']['ArtistWithAlbums'], 'theme_primary_color' | 'theme_accent_color' | 'theme_background_color'>
type Role = 'primary' | 'accent' | 'background'

interface Alias { name: string, step: number }
interface Block { selector: string, prefix?: string, pinStep?: number, keepLightSideNatural?: boolean, aliases?: Alias[] }

// fw.css redefines --color-focus and --color-link/-hover/-active locally
// within these selectors (its own :root/.color-default* block, and again
// within .color-primary*), so an override at :root alone gets shadowed
// there -- e.g. App.vue's <RouterView> itself carries .color-default-solid,
// which re-sets --color-link for everything inside it. Match the same
// selectors to win wherever such an element actually sits.
const LOCAL_SCALE_OVERRIDE_CLASSES = ['.color-default', '.color-default-solid', '.color-default-ghost', '.color-primary', '.color-primary-solid', '.color-primary-ghost']

// Each role emits up to two blocks:
// - class-scoped: overrides --color-* on .color-{role}* (what fw.css's own
//   component classes read), pinned at 550 (fw.css's solid-fill step) so a
//   button's fill is exactly the picked colour.
// - a scope-level constant --color-{role}-*, for code not wrapped in a
//   .color-{role}* class (legacy _player/_queue sliders, Nav.vue's active-tab
//   underline), pinned at 500, the step those consumers read.
// Per-role pins and aliases are noted inline.
const ROLE_BLOCKS: Record<Role, (scope: string) => Block[]> = {
  primary: scope => [
    { selector: ['.color-primary', '.color-primary-solid', '.color-primary-ghost'].map(c => `${scope} ${c}`).join(', '), prefix: 'color', pinStep: 550 },
    { selector: scope, prefix: 'color-primary', pinStep: 500 },
    // fw.css's own `a[href]` rule reads --color-link/-hover/-active for plain
    // links (e.g. in a markdown bio); pointing them at this ramp keeps that
    // rule's underline/hover handling, just tinted. 600/650 mirror fw.css's
    // own +50/+100 hover/active convention. Emitted across
    // LOCAL_SCALE_OVERRIDE_CLASSES because fw.css redefines these locally too.
    { selector: [scope, ...LOCAL_SCALE_OVERRIDE_CLASSES.map(c => `${scope} ${c}`)].join(', '), pinStep: 500, aliases: [
      { name: 'color-link', step: 500 },
      { name: 'color-link-hover', step: 600 },
      { name: 'color-link-active', step: 650 }
    ] }
  ],
  accent: scope => [
    { selector: ['.color-accent', '.color-accent-solid', '.color-accent-ghost'].map(c => `${scope} ${c}`).join(', '), prefix: 'color', pinStep: 550 },
    { selector: scope, prefix: 'color-accent', pinStep: 500 },
    // --color-focus (fw.css's :focus-visible outline) at step 450, the step
    // fw.css itself uses within a role scope; across LOCAL_SCALE_OVERRIDE_CLASSES
    // to beat those local redefinitions.
    { selector: [scope, ...LOCAL_SCALE_OVERRIDE_CLASSES.map(c => `${scope} ${c}`)].join(', '), pinStep: 500, aliases: [{ name: 'color-focus', step: 450 }] }
  ],
  // Constant form only (fw.css defines --color-default-* once at :root). Pinned
  // at 800 + keepLightSideNatural: dark mode's page background mirrors step
  // 200->800, so a dark pick can reach dark in dark mode without shifting the
  // light end.
  background: scope => [
    { selector: scope, prefix: 'color-default', pinStep: 800, keepLightSideNatural: true }
  ]
}

const toDeclarations = (colorHex: string, prefix?: string, pinStep?: number, keepLightSideNatural?: boolean, aliases: Alias[] = []): string => {
  const ramp = synthesizeRamp(colorHex, pinStep, keepLightSideNatural)
  const rampLines = prefix === undefined ? [] : ramp.map((step, index) => {
    const dark = at(ramp, ramp.length - 1 - index)
    return `  --${prefix}-${step.step}: light-dark(${step.oklch}, ${dark.oklch});`
  })
  const aliasLines = aliases.map(({ name, step: aliasStep }) => {
    const lightIndex = aliasStep / 50 - 1
    const darkIndex = ramp.length - 1 - lightIndex
    return `  --${name}: light-dark(${at(ramp, lightIndex).oklch}, ${at(ramp, darkIndex).oklch});`
  })
  return [...rampLines, ...aliasLines].join('\n')
}

/**
 * Generates a CSS override block for the picked theme colors, scoped under
 * `scope` (":root" for an instance-wide theme, or a wrapper selector to keep
 * an artist/channel theme from leaking into the sidebar or other pages).
 * Code reading a shade directly rather than via the .color-* classes falls
 * back to fw.css's own default when unthemed: --color-accent-* and
 * --color-default-* are defined globally in fw.css, so e.g. a bare
 * `var(--color-accent-500)` is already safe.
 *
 * @param colors - Picked theme colors (Artist's own theme_*_color fields); roles left unset/null are omitted from the output.
 * @param scope - CSS selector the override is scoped under. Defaults to `:root` for an instance-wide theme.
 */
export const generateThemeCss = (colors: ThemeColors, scope = ':root'): string => {
  const roleColors: Record<Role, string | null> = {
    primary: colors.theme_primary_color,
    accent: colors.theme_accent_color,
    background: colors.theme_background_color
  }

  return (Object.entries(roleColors) as [Role, string | null][])
    .filter((entry): entry is [Role, string] => Boolean(entry[1]))
    .flatMap(([role, colorHex]) =>
      ROLE_BLOCKS[role](scope).map(({ selector, prefix, pinStep, keepLightSideNatural, aliases }) => `${selector} {\n${toDeclarations(colorHex, prefix, pinStep, keepLightSideNatural, aliases)}\n}`)
    )
    .join('\n\n')
}
